<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import format from 'date-fns/format';

import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

// Mode toggle
const mode = ref('basico');

// Data state
const radarStats = ref(null);
const highlightedComments = ref([]);
const allComments = ref([]);
const timelineEvents = ref([]);
const isLoading = ref(false);
const searchQuery = ref('');

// Computed
const isCompleto = computed(() => mode.value === 'completo');

const filteredComments = computed(() => {
  if (!searchQuery.value.trim()) return allComments.value;
  const q = searchQuery.value.toLowerCase();
  return allComments.value.filter(
    c =>
      (c.author && c.author.toLowerCase().includes(q)) ||
      (c.text && c.text.toLowerCase().includes(q)) ||
      (c.topic && c.topic.toLowerCase().includes(q))
  );
});

// Helper functions
function sentimentClass(sentiment) {
  if (sentiment === 'positive') return 'bg-n-teal-9';
  if (sentiment === 'negative') return 'bg-n-ruby-9';
  return 'bg-n-amber-9';
}

function sentimentLabel(sentiment) {
  if (sentiment === 'positive') return 'Positivo';
  if (sentiment === 'negative') return 'Negativo';
  return 'Neutro';
}

function riskClass(risk) {
  if (risk === 'low') return 'bg-n-slate-9';
  if (risk === 'medium') return 'bg-n-amber-9';
  if (risk === 'high') return 'bg-n-orange-9';
  if (risk === 'critical') return 'bg-n-ruby-9';
  return 'bg-n-slate-9';
}

function statusClass(status) {
  if (status === 'resolved') return 'bg-n-teal-3 text-n-teal-11';
  return 'bg-n-amber-3 text-n-amber-11';
}

function formatDate(dateStr) {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm');
  } catch {
    return dateStr;
  }
}

// API functions
async function fetchRadarData() {
  if (!props.selectedInbox) return;
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getRadarStats({
      inboxId: props.selectedInbox.id,
    });
    const data = response.data;
    radarStats.value = data.stats || null;
    highlightedComments.value = data.highlighted_comments || [];
    allComments.value = data.all_comments || [];
    timelineEvents.value = data.timeline_events || [];
  } catch {
    radarStats.value = null;
    highlightedComments.value = [];
    allComments.value = [];
    timelineEvents.value = [];
  } finally {
    isLoading.value = false;
  }
}

function loadData() {
  if (props.selectedInbox) {
    fetchRadarData();
  }
}

watch(() => props.selectedInbox, loadData);

onMounted(loadData);
</script>

<template>
  <div>
    <div
      v-if="!selectedInbox"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_SELECT_INBOX') }}
    </div>

    <template v-else>
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-12"
      >
        <Spinner />
      </div>

      <template v-else>
        <!-- Mode toggle -->
        <div class="flex items-center gap-3 mb-4">
          <button
            class="px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors"
            :class="mode === 'basico' ? 'bg-n-blue-9 text-white border-n-blue-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'"
            @click="mode = 'basico'"
          >
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_MODE_BASIC') }}
          </button>
          <button
            class="px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors"
            :class="mode === 'completo' ? 'bg-n-blue-9 text-white border-n-blue-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'"
            @click="mode = 'completo'"
          >
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_MODE_COMPLETE') }}
          </button>
        </div>

        <!-- Status banner -->
        <div
          class="rounded-lg p-3 mb-6 border"
          :class="isCompleto ? 'bg-n-teal-2 border-n-teal-7' : 'bg-n-amber-2 border-n-amber-7'"
        >
          <p
            class="text-sm font-medium"
            :class="isCompleto ? 'text-n-teal-11' : 'text-n-amber-11'"
          >
            {{ isCompleto ? $t('INSTAGRAM_MEDIA_REPORTS.RADAR_STATUS_COMPLETE') : $t('INSTAGRAM_MEDIA_REPORTS.RADAR_STATUS_BASIC') }}
          </p>
        </div>

        <!-- Summary cards -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
            <p class="text-xs text-n-slate-11 mb-1">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_SENTIMENT') }}
            </p>
            <div v-if="radarStats" class="flex items-center justify-center gap-1 mt-1">
              <span class="text-xs px-1.5 py-0.5 rounded-full bg-n-teal-3 text-n-teal-11">{{ radarStats.sentiment_positive || 0 }}</span>
              <span class="text-xs px-1.5 py-0.5 rounded-full bg-n-amber-3 text-n-amber-11">{{ radarStats.sentiment_neutral || 0 }}</span>
              <span class="text-xs px-1.5 py-0.5 rounded-full bg-n-ruby-3 text-n-ruby-11">{{ radarStats.sentiment_negative || 0 }}</span>
            </div>
            <p v-else class="text-xl font-semibold text-n-slate-12">-</p>
          </div>
          <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
            <p class="text-xs text-n-slate-11 mb-1">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_TOP_TOPICS') }}
            </p>
            <p class="text-xl font-semibold text-n-slate-12">
              {{ radarStats ? (radarStats.top_topics_count || 0) : '-' }}
            </p>
          </div>
          <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
            <p class="text-xs text-n-slate-11 mb-1">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_RECURRING_AUTHORS') }}
            </p>
            <p class="text-xl font-semibold text-n-slate-12">
              {{ radarStats ? (radarStats.recurring_authors_count || 0) : '-' }}
            </p>
          </div>
          <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
            <p class="text-xs text-n-slate-11 mb-1">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_RECENT_VELOCITY') }}
            </p>
            <p class="text-xl font-semibold text-n-slate-12">
              {{ radarStats ? (radarStats.recent_velocity || 0) : '-' }}
            </p>
          </div>
        </div>

        <!-- Highlighted comments -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_HIGHLIGHTED_COMMENTS') }}
          </h3>
          <div v-if="highlightedComments.length" class="space-y-3">
            <div
              v-for="comment in highlightedComments"
              :key="comment.id"
              class="flex items-start gap-4 p-3 rounded-lg border border-n-weak"
            >
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1 flex-wrap">
                  <span class="text-sm font-medium text-n-slate-12">@{{ comment.author }}</span>
                  <span
                    class="text-xs px-2 py-0.5 rounded-full text-white"
                    :class="sentimentClass(comment.sentiment)"
                  >
                    {{ sentimentLabel(comment.sentiment) }}
                  </span>
                  <span
                    class="text-xs px-2 py-0.5 rounded-full text-white"
                    :class="riskClass(comment.risk_level)"
                  >
                    {{ comment.risk_level || 'N/A' }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    &#x2764; {{ comment.likes_count || 0 }}
                  </span>
                </div>
                <p class="text-sm text-n-slate-11">{{ comment.text }}</p>
                <p class="text-xs text-n-slate-9 mt-1">{{ formatDate(comment.created_at) }}</p>
              </div>
            </div>
          </div>
          <div v-else class="flex items-center justify-center py-8 text-n-slate-11">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_NO_HIGHLIGHTED') }}
          </div>
        </div>

        <!-- All analyzed comments -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_ALL_COMMENTS') }}
          </h3>
          <div class="flex items-center justify-between mb-4">
            <input
              v-model="searchQuery"
              type="text"
              class="py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12 w-64"
              :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.RADAR_SEARCH_PLACEHOLDER')"
            />
            <span class="text-xs text-n-slate-11">
              {{ filteredComments.length }} {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_ITEMS') }}
            </span>
          </div>
          <div v-if="filteredComments.length" class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-n-weak">
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_COL_AUTHOR') }}
                  </th>
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_COL_COMMENT') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_COL_SENTIMENT') }}
                  </th>
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_COL_TOPIC') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_COL_RISK') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_COL_PUBLISHED') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="comment in filteredComments"
                  :key="comment.id"
                  class="border-b border-n-weak"
                >
                  <td class="py-2 px-3 text-n-slate-12 font-medium">@{{ comment.author }}</td>
                  <td class="py-2 px-3 text-n-slate-11 max-w-xs truncate">{{ comment.text }}</td>
                  <td class="py-2 px-3 text-center">
                    <span
                      class="text-xs px-2 py-0.5 rounded-full text-white"
                      :class="sentimentClass(comment.sentiment)"
                    >
                      {{ sentimentLabel(comment.sentiment) }}
                    </span>
                  </td>
                  <td class="py-2 px-3 text-n-slate-11 text-xs">{{ comment.topic || '-' }}</td>
                  <td class="py-2 px-3 text-center">
                    <span
                      class="text-xs px-2 py-0.5 rounded-full text-white"
                      :class="riskClass(comment.risk_level)"
                    >
                      {{ comment.risk_level || 'N/A' }}
                    </span>
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-11 text-xs">
                    {{ comment.created_at ? formatDate(comment.created_at) : '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="flex items-center justify-center py-8 text-n-slate-11">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_NO_COMMENTS') }}
          </div>
        </div>

        <!-- Timeline -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_TIMELINE_TITLE') }}
          </h3>
          <div v-if="timelineEvents.length" class="space-y-3">
            <div
              v-for="event in timelineEvents"
              :key="event.id"
              class="flex items-start gap-4 p-3 rounded-lg border border-n-weak"
            >
              <div class="flex-1 min-w-0">
                <p class="text-sm text-n-slate-12">{{ event.description }}</p>
                <div class="flex items-center gap-2 mt-1 flex-wrap">
                  <span
                    class="text-xs px-2 py-0.5 rounded-full"
                    :class="statusClass(event.status)"
                  >
                    {{ event.status === 'resolved' ? $t('INSTAGRAM_MEDIA_REPORTS.RADAR_STATUS_RESOLVED') : $t('INSTAGRAM_MEDIA_REPORTS.RADAR_STATUS_MONITORING') }}
                  </span>
                  <span
                    v-for="category in (event.categories || [])"
                    :key="category"
                    class="text-xs px-2 py-0.5 rounded-full bg-n-violet-3 text-n-violet-11"
                  >
                    {{ category }}
                  </span>
                  <span class="text-xs text-n-slate-9">
                    {{ event.created_at ? formatDate(event.created_at) : '-' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="flex items-center justify-center py-8 text-n-slate-11">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RADAR_NO_TIMELINE') }}
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
